import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Sparkles } from 'lucide-react';
import { api, fmtMoney } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const GST_RATES = [0, 0.1, 0.25, 3, 5, 12, 18, 28];

// Sprint 2 doc-type meta so the editor can label itself for Quotes / CN / DN.
const DOC_META = {
    invoice:          { title: 'Invoice', new: 'New Invoice', detailRoute: 'invoices' },
    quote:            { title: 'Quote', new: 'New Quote', detailRoute: 'quotes' },
    credit_note:      { title: 'Credit Note', new: 'New Credit Note', detailRoute: 'credit-notes' },
    debit_note:       { title: 'Debit Note', new: 'New Debit Note', detailRoute: 'debit-notes' },
    delivery_challan: { title: 'Delivery Challan', new: 'New Delivery Challan', detailRoute: 'delivery-challans' },
    sales_order:      { title: 'Sales Order', new: 'New Sales Order', detailRoute: 'sales-orders' },
    purchase_order:   { title: 'Purchase Order', new: 'New Purchase Order', detailRoute: 'purchase-orders' },
};

function todayIso() { return new Date().toISOString().slice(0, 10); }
function addDaysIso(iso, n) {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}

export default function InvoiceEditPage({ isNew, docType = 'invoice' }) {
    const nav = useNavigate();
    const { id } = useParams();
    const meta = DOC_META[docType] || DOC_META.invoice;
    const [customers, setCustomers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [existing, setExisting] = useState(null);  // for edit mode, if not draft show read-only banner
    const [form, setForm] = useState({
        customer_id: '',
        invoice_date: todayIso(),
        due_date: addDaysIso(todayIso(), 30),
        po_number: '',
        po_date: '',
        line_items: [{ description: '', hsn_sac: '', quantity: 1, unit: 'nos', rate: 0, discount_percent: 0, gst_rate: 18 }],
        global_discount_percent: 0,
        shipping_charges: 0,
        other_charges: 0,
        // Sprint 2026-09 — TDS / TCS fields. Only applied to `doc_type=invoice`.
        tds_percent: 0,
        tds_section: '',
        tcs_percent: 0,
        tcs_section: '',
        notes_to_customer: '',
        terms_conditions: '',
        template_id: 'classic',
    });

    useEffect(() => {
        api.invListCustomers({ limit: 200 }).then((r) => setCustomers(r.items || [])).catch(() => {});
        if (!isNew && id) {
            api.invGetInvoice(id).then((inv) => {
                setExisting(inv);
                setForm({
                    customer_id: inv.customer_id,
                    invoice_date: inv.invoice_date,
                    due_date: inv.due_date,
                    po_number: inv.po_number || '',
                    po_date: inv.po_date || '',
                    line_items: (inv.line_items || []).map((l) => ({
                        description: l.description || '',
                        hsn_sac: l.hsn_sac || '',
                        quantity: l.quantity || 1,
                        unit: l.unit || 'nos',
                        rate: l.rate || 0,
                        discount_percent: l.discount_percent || 0,
                        gst_rate: l.gst_rate ?? 18,
                    })),
                    global_discount_percent: inv.global_discount_percent || 0,
                    shipping_charges: inv.shipping_charges || 0,
                    other_charges: inv.other_charges || 0,
                    tds_percent: inv.tds_percent || 0,
                    tds_section: inv.tds_section || '',
                    tcs_percent: inv.tcs_percent || 0,
                    tcs_section: inv.tcs_section || '',
                    notes_to_customer: inv.notes_to_customer || '',
                    terms_conditions: inv.terms_conditions || '',
                    template_id: inv.template_id || 'classic',
                });
            }).catch(() => toast.error('Failed to load invoice'));
        }
    }, [id, isNew]);

    // Live computed totals (client-side approx; server is source of truth)
    const totals = useMemo(() => {
        let subtotal = 0, gstTotal = 0;
        for (const l of form.line_items) {
            const gross = Number(l.quantity || 0) * Number(l.rate || 0);
            const disc = gross * (Number(l.discount_percent || 0) / 100);
            const taxable = gross - disc;
            const gst = taxable * (Number(l.gst_rate || 0) / 100);
            subtotal += taxable;
            gstTotal += gst;
        }
        const g = subtotal * (Number(form.global_discount_percent || 0) / 100);
        const scale = subtotal > 0 ? (subtotal - g) / subtotal : 0;
        const taxableAfter = subtotal - g;
        const gstAfter = gstTotal * scale;
        const shipping = Number(form.shipping_charges || 0);
        const other = Number(form.other_charges || 0);
        const raw = taxableAfter + gstAfter + shipping + other;
        const grand = Math.round(raw);
        const roundoff = grand - raw;
        // Sprint 2026-09 — TDS / TCS live preview.
        // TDS is applied on taxable value only; TCS on the grand_total.
        const tdsPct = Number(form.tds_percent || 0);
        const tcsPct = Number(form.tcs_percent || 0);
        const tds = docType === 'invoice' && tdsPct > 0 ? +(taxableAfter * tdsPct / 100).toFixed(2) : 0;
        const tcs = docType === 'invoice' && tcsPct > 0 ? +(grand * tcsPct / 100).toFixed(2) : 0;
        const netReceivable = +(grand - tds + tcs).toFixed(2);
        return {
            subtotal: subtotal, discount: g, taxable: taxableAfter,
            gst: gstAfter, shipping, other, roundoff, grand,
            tds, tcs, netReceivable,
        };
    }, [form, docType]);

    const setLine = (i, k, v) => {
        setForm((f) => ({
            ...f,
            line_items: f.line_items.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)),
        }));
    };
    const addLine = () => setForm((f) => ({
        ...f,
        line_items: [...f.line_items, { description: '', hsn_sac: '', quantity: 1, unit: 'nos', rate: 0, discount_percent: 0, gst_rate: 18 }],
    }));
    const removeLine = (i) => setForm((f) => ({
        ...f,
        line_items: f.line_items.filter((_, idx) => idx !== i),
    }));

    // Sprint 2: item-catalog autocomplete. As the user types in the
    // description field, hit /invoicing/items/suggest with a 200ms debounce
    // to fetch past line items. Picking a suggestion auto-fills rate,
    // HSN, GST, and unit so the user only enters quantity.
    const [suggestState, setSuggestState] = useState({ line: null, items: [], open: false });
    const suggestTimer = useRef(null);
    const debouncedSuggest = (i, val) => {
        setSuggestState((s) => ({ ...s, line: i }));
        if (suggestTimer.current) clearTimeout(suggestTimer.current);
        const q = (val || '').trim();
        if (q.length < 2) {
            setSuggestState({ line: null, items: [], open: false });
            return;
        }
        suggestTimer.current = setTimeout(async () => {
            try {
                const items = await api.invSuggestItems(q, 6);
                setSuggestState({ line: i, items, open: items.length > 0 });
            } catch (e) {
                setSuggestState({ line: null, items: [], open: false });
            }
        }, 200);
    };
    const pickSuggestion = (i, sug) => {
        setForm((f) => ({
            ...f,
            line_items: f.line_items.map((l, idx) => idx === i ? {
                ...l,
                description: sug.description || l.description,
                hsn_sac: sug.hsn_sac || l.hsn_sac,
                unit: sug.unit || l.unit,
                rate: Number(sug.rate) || l.rate,
                gst_rate: Number(sug.gst_rate ?? l.gst_rate),
            } : l),
        }));
        setSuggestState({ line: null, items: [], open: false });
    };

    const readOnly = existing && existing.status !== 'draft';

    const onSave = async () => {
        if (!form.customer_id) { toast.error('Select a customer'); return; }
        if (form.line_items.length === 0 || form.line_items.every((l) => !l.description)) {
            toast.error('Add at least one line item');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                line_items: form.line_items.filter((l) => l.description && (Number(l.quantity) > 0)),
                po_date: form.po_date || null,
                // Coerce to numbers so pydantic doesn't reject stringified inputs.
                tds_percent: Number(form.tds_percent || 0),
                tcs_percent: Number(form.tcs_percent || 0),
                tds_section: form.tds_section || null,
                tcs_section: form.tcs_section || null,
            };
            if (isNew) {
                let inv;
                if (docType === 'invoice') {
                    inv = await api.invCreateInvoice(payload);
                } else {
                    inv = await api.invCreateDoc(docType, payload);
                }
                toast.success('Draft saved');
                nav(`/invoicing/${meta.detailRoute}/${inv.id}`);
            } else {
                await api.invUpdateInvoice(id, payload);
                toast.success('Draft updated');
                nav(`/invoicing/${meta.detailRoute}/${id}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="invoice-edit-page">
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-back"><ArrowLeft size={16} /></Button>
                <h1 className="text-2xl font-heading font-semibold tracking-tight">
                    {isNew ? meta.new : (readOnly ? `${meta.title} ${existing?.invoice_number || ''}` : `Edit ${meta.title}`)}
                </h1>
            </div>

            {readOnly && (
                <Card className="border-amber-200 bg-amber-50/40 dark:bg-amber-950/20">
                    <CardContent className="pt-4 pb-4 text-sm">
                        This invoice has been issued and cannot be edited. Use “Duplicate” from the detail page to create a similar one.
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <Card>
                        <CardHeader><CardTitle className="text-base">Customer &amp; Dates</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <Label className="text-xs text-muted-foreground">Customer *</Label>
                                    <Select value={form.customer_id} onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v }))} disabled={readOnly}>
                                        <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Select a customer" /></SelectTrigger>
                                        <SelectContent>
                                            {customers.map((c) => (
                                                <SelectItem key={c.id} value={c.id} data-testid={`customer-opt-${c.id}`}>{c.name}{c.gstin ? ` · ${c.gstin}` : ''}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {customers.length === 0 && <div className="text-xs text-muted-foreground mt-1">No customers yet. <a href="/invoicing/customers/new" className="text-primary underline">Create one</a> first.</div>}
                                </div>
                                <div><Label className="text-xs text-muted-foreground">Invoice date</Label><Input data-testid="input-inv-date" type="date" value={form.invoice_date} onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))} disabled={readOnly} /></div>
                                <div><Label className="text-xs text-muted-foreground">Due date</Label><Input data-testid="input-due-date" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} disabled={readOnly} /></div>
                                <div><Label className="text-xs text-muted-foreground">PO Number</Label><Input data-testid="input-po" value={form.po_number} onChange={(e) => setForm((f) => ({ ...f, po_number: e.target.value }))} placeholder="Optional" disabled={readOnly} /></div>
                                <div><Label className="text-xs text-muted-foreground">PO Date</Label><Input data-testid="input-po-date" type="date" value={form.po_date || ''} onChange={(e) => setForm((f) => ({ ...f, po_date: e.target.value }))} disabled={readOnly} /></div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Line Items</CardTitle>
                            {!readOnly && (
                                <Button variant="outline" size="sm" onClick={addLine} data-testid="btn-add-line"><Plus size={14} className="mr-1" /> Add line</Button>
                            )}
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {form.line_items.map((l, i) => (
                                <div key={i} data-testid={`line-item-${i}`} className="grid grid-cols-12 gap-2 items-end pb-3 border-b border-border/50 last:border-0">
                                    <div className="col-span-12 md:col-span-4">
                                        <Label className="text-[10px] text-muted-foreground uppercase">Description</Label>
                                        <div className="relative">
                                            <Input
                                                data-testid={`line-desc-${i}`}
                                                value={l.description}
                                                onChange={(e) => {
                                                    setLine(i, 'description', e.target.value);
                                                    if (!readOnly) debouncedSuggest(i, e.target.value);
                                                }}
                                                onFocus={() => l.description.length >= 2 && debouncedSuggest(i, l.description)}
                                                onBlur={() => setTimeout(() => setSuggestState({ line: null, items: [], open: false }), 200)}
                                                disabled={readOnly}
                                                placeholder="Item / service"
                                                autoComplete="off"
                                            />
                                            {suggestState.open && suggestState.line === i && (
                                                <div
                                                    className="absolute left-0 right-0 top-full mt-1 z-30 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
                                                    data-testid={`line-suggest-${i}`}
                                                >
                                                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/60 flex items-center gap-1.5">
                                                        <Sparkles size={11} /> Recent items you've used
                                                    </div>
                                                    {suggestState.items.map((sug, si) => (
                                                        <button
                                                            key={si}
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => pickSuggestion(i, sug)}
                                                            className="w-full text-left px-3 py-2 hover:bg-accent flex justify-between items-center gap-2"
                                                            data-testid={`suggest-${i}-${si}`}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium truncate">{sug.description}</div>
                                                                <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
                                                                    {sug.hsn_sac && <span className="font-mono">HSN {sug.hsn_sac}</span>}
                                                                    <span>@ {sug.gst_rate ?? 18}% GST</span>
                                                                    <span>· used {sug.used_count}×</span>
                                                                </div>
                                                            </div>
                                                            <div className="text-sm font-semibold tabular-nums text-primary shrink-0">
                                                                ₹{Number(sug.rate || 0).toLocaleString('en-IN')}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-span-6 md:col-span-1">
                                        <Label className="text-[10px] text-muted-foreground uppercase">HSN</Label>
                                        <Input data-testid={`line-hsn-${i}`} value={l.hsn_sac || ''} onChange={(e) => setLine(i, 'hsn_sac', e.target.value)} disabled={readOnly} className="font-mono text-xs" />
                                    </div>
                                    <div className="col-span-3 md:col-span-1">
                                        <Label className="text-[10px] text-muted-foreground uppercase">Qty</Label>
                                        <Input data-testid={`line-qty-${i}`} type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} disabled={readOnly} />
                                    </div>
                                    <div className="col-span-3 md:col-span-1">
                                        <Label className="text-[10px] text-muted-foreground uppercase">Unit</Label>
                                        <Input data-testid={`line-unit-${i}`} value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} disabled={readOnly} />
                                    </div>
                                    <div className="col-span-4 md:col-span-2">
                                        <Label className="text-[10px] text-muted-foreground uppercase">Rate</Label>
                                        <Input data-testid={`line-rate-${i}`} type="number" step="0.01" value={l.rate} onChange={(e) => setLine(i, 'rate', e.target.value)} disabled={readOnly} />
                                    </div>
                                    <div className="col-span-4 md:col-span-1">
                                        <Label className="text-[10px] text-muted-foreground uppercase">Disc%</Label>
                                        <Input data-testid={`line-disc-${i}`} type="number" step="0.01" value={l.discount_percent} onChange={(e) => setLine(i, 'discount_percent', e.target.value)} disabled={readOnly} />
                                    </div>
                                    <div className="col-span-3 md:col-span-1">
                                        <Label className="text-[10px] text-muted-foreground uppercase">GST%</Label>
                                        <Select value={String(l.gst_rate)} onValueChange={(v) => setLine(i, 'gst_rate', Number(v))} disabled={readOnly}>
                                            <SelectTrigger data-testid={`line-gst-${i}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {GST_RATES.map((r) => (
                                                    <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {!readOnly && (
                                        <div className="col-span-1">
                                            <Button variant="ghost" size="icon" onClick={() => removeLine(i)} data-testid={`line-remove-${i}`} className="text-red-600"><Trash2 size={14} /></Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader><CardTitle className="text-base">Additional Charges &amp; Notes</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div><Label className="text-xs text-muted-foreground">Global discount %</Label><Input data-testid="input-global-disc" type="number" step="0.01" value={form.global_discount_percent} onChange={(e) => setForm((f) => ({ ...f, global_discount_percent: e.target.value }))} disabled={readOnly} /></div>
                                <div><Label className="text-xs text-muted-foreground">Shipping charges</Label><Input data-testid="input-shipping" type="number" step="0.01" value={form.shipping_charges} onChange={(e) => setForm((f) => ({ ...f, shipping_charges: e.target.value }))} disabled={readOnly} /></div>
                                <div><Label className="text-xs text-muted-foreground">Other charges</Label><Input data-testid="input-other" type="number" step="0.01" value={form.other_charges} onChange={(e) => setForm((f) => ({ ...f, other_charges: e.target.value }))} disabled={readOnly} /></div>
                            </div>
                            {/* Sprint 2026-09: TDS / TCS inputs. Only shown on Invoices —
                                Quotes / SO / PO / Challans don't need withholding math. */}
                            {docType === 'invoice' && (
                                <div className="pt-3 mt-1 border-t border-dashed border-border/60">
                                    <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">TDS / TCS</div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div>
                                            <Label className="text-xs text-muted-foreground">TDS %</Label>
                                            <Input data-testid="input-tds-percent" type="number" step="0.01" placeholder="0" value={form.tds_percent} onChange={(e) => setForm((f) => ({ ...f, tds_percent: e.target.value }))} disabled={readOnly} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">TDS section</Label>
                                            <Input data-testid="input-tds-section" placeholder="194J / 194C …" value={form.tds_section} onChange={(e) => setForm((f) => ({ ...f, tds_section: e.target.value }))} disabled={readOnly} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">TCS %</Label>
                                            <Input data-testid="input-tcs-percent" type="number" step="0.01" placeholder="0" value={form.tcs_percent} onChange={(e) => setForm((f) => ({ ...f, tcs_percent: e.target.value }))} disabled={readOnly} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">TCS section</Label>
                                            <Input data-testid="input-tcs-section" placeholder="206C(1H)" value={form.tcs_section} onChange={(e) => setForm((f) => ({ ...f, tcs_section: e.target.value }))} disabled={readOnly} />
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-1.5">
                                        TDS is deducted <em>by the customer</em>; TCS is collected <em>from</em> them on top of the invoice.
                                    </div>
                                </div>
                            )}
                            <div>
                                <Label className="text-xs text-muted-foreground">Notes to customer</Label>
                                <Textarea data-testid="input-notes" rows={2} value={form.notes_to_customer} onChange={(e) => setForm((f) => ({ ...f, notes_to_customer: e.target.value }))} disabled={readOnly} />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Terms &amp; Conditions</Label>
                                <Textarea data-testid="input-terms" rows={3} value={form.terms_conditions} onChange={(e) => setForm((f) => ({ ...f, terms_conditions: e.target.value }))} disabled={readOnly} placeholder="Leave blank to use default from settings" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Live totals */}
                <div className="lg:col-span-1">
                    <Card className="sticky top-4">
                        <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <Row lbl="Subtotal (taxable)">{fmtMoney(totals.subtotal)}</Row>
                            {totals.discount > 0 && <Row lbl={`Global discount (${form.global_discount_percent}%)`} negative>{fmtMoney(-totals.discount)}</Row>}
                            {totals.shipping > 0 && <Row lbl="Shipping">+ {fmtMoney(totals.shipping)}</Row>}
                            {totals.other > 0 && <Row lbl="Other charges">+ {fmtMoney(totals.other)}</Row>}
                            <Row lbl="GST">+ {fmtMoney(totals.gst)}</Row>
                            <Row lbl="Round off">{fmtMoney(totals.roundoff)}</Row>
                            <div className="border-t border-border/60 pt-2 mt-2">
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold">Grand Total</span>
                                    <span className="font-heading text-xl font-semibold text-primary" data-testid="summary-grand-total">{fmtMoney(totals.grand)}</span>
                                </div>
                            </div>
                            {docType === 'invoice' && (totals.tds > 0 || totals.tcs > 0) && (
                                <div className="border-t border-dashed border-border/60 pt-2 mt-1 space-y-1">
                                    {totals.tds > 0 && <Row lbl={`Less: TDS${form.tds_section ? ` (${form.tds_section})` : ''} @ ${form.tds_percent}%`} negative>- {fmtMoney(totals.tds)}</Row>}
                                    {totals.tcs > 0 && <Row lbl={`Add: TCS${form.tcs_section ? ` (${form.tcs_section})` : ''} @ ${form.tcs_percent}%`}>+ {fmtMoney(totals.tcs)}</Row>}
                                    <div className="flex items-center justify-between pt-1">
                                        <span className="font-semibold text-sm">Net Receivable</span>
                                        <span className="font-heading text-base font-semibold text-emerald-700 dark:text-emerald-300" data-testid="summary-net-receivable">{fmtMoney(totals.netReceivable)}</span>
                                    </div>
                                </div>
                            )}
                            {!readOnly && (
                                <div className="pt-4 space-y-2">
                                    <Button onClick={onSave} disabled={saving} className="w-full" data-testid="btn-save-draft">
                                        <Save size={16} className="mr-1.5" /> {saving ? 'Saving…' : (isNew ? 'Save draft' : 'Update draft')}
                                    </Button>
                                    <div className="text-xs text-muted-foreground text-center">Issue the invoice from the detail page after saving.</div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function Row({ lbl, children, negative }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{lbl}</span>
            <span className={`tabular-nums ${negative ? 'text-red-600' : ''}`}>{children}</span>
        </div>
    );
}
